import { Placement } from '@popperjs/core';
import React, { FC, ReactNode, useContext, useRef } from 'react';
import { TriggerType, usePopperTooltip } from 'react-popper-tooltip';
import { Portal } from '../../core/Portal';
import { Box } from '../Box';
import { Text } from '../Text';
import { Animation, ITransitionClasses } from '../../core/Animation';
import { IDefaultComponentProps } from '@src/models/props';
import { classnames } from '@src/utils/classnames';
import { getClass } from '@src/utils/getClass';

import styled from './Tooltip.module.scss';
import { AppContext } from '../../providers/App/AppContext';

export interface ITooltipProps extends IDefaultComponentProps {
  /** Расположение поповера */
  placement?: Placement;

  /** Содержание тултипа */
  content: ReactNode | string;

  /**  Событие на которое будет реагировать тултип */
  trigger?: TriggerType;

  /** Смещение тултипа [x, y] */
  offset?: [number, number];

  /** Обычно при наведении на тултип он закрывается, данное свойство может отключить этот эффект */
  interactive?: boolean;

  /** Цвет тултипа */
  view?: 'basicBlack' | 'basicWhite';

  /** Максимальная ширина тултипа для typeof content === 'string' */
  maxWidth?: number;

  /**
   * Свойство для добавления inline-flex для обертки.
   * Иногда это требуется для того чтобы children не переносился на новую строку из-за обернутого div
   */
  inline?: boolean;

  open?: boolean;
}

/**
 * Компонент для отображения тултипа
 * @deprecated используй новый тултип `<TooltipNew />`
 * Обратите внимание, что новый тултип может сломать верстку. Более читай в описании компонента
 */
export const Tooltip: FC<ITooltipProps> = ({
  children,
  placement = 'top',
  trigger,
  content,
  offset,
  interactive = true,
  view = 'basicBlack',
  className,
  style,
  maxWidth,
  inline,
  open = true,
}) => {
  const { mode } = useContext(AppContext);

  const { visible, setTriggerRef, setTooltipRef, getTooltipProps, getArrowProps } = usePopperTooltip({
    trigger,
    placement,
    offset,
    interactive,
  });

  const prevVisible = useRef(visible);

  const transitionClasses: ITransitionClasses = {
    appear: styled.rootEnter,
    appearActive: styled.rootEnterActive,
    enter: styled.rootEnter,
    enterActive: styled.rootEnterActive,
    exit: styled.rootExit,
    exitActive: styled.rootExitActive,
  };

  const classes = 'Tooltip__root ' + classnames(className, getClass(styled, 'view', view));
  const wrapperClasses = classnames({
    [styled.wrapperInline]: inline,
  });

  let currentVisible = visible;

  // в dev mode в момент когда нужно закрыть тултип,
  // мы будем отменять это значение на то,
  // что указано в `window.__CENTRA__.AUTO_TOOLTIP_CLOSE`
  if (typeof window !== 'undefined' && mode === 'dev' && typeof window.__CENTRA__?.AUTO_TOOLTIP_CLOSE === 'boolean') {
    if (prevVisible.current && !visible) {
      currentVisible = !window.__CENTRA__.AUTO_TOOLTIP_CLOSE;
    }

    prevVisible.current = visible;
  }

  return (
    <>
      <div className={wrapperClasses} ref={setTriggerRef}>
        {children}
      </div>
      {content && (
        <Portal>
          <Animation open={open && currentVisible} mountOnEnter unmountOnExit transitionClasses={transitionClasses}>
            <div tabIndex={0} ref={setTooltipRef} {...getTooltipProps({ className: classes, style })}>
              <div style={{ position: 'relative' }}>
                <div {...getArrowProps({ className: styled.arrow })} />
                {typeof content === 'string' ? (
                  <Box style={{ maxWidth }}>
                    <Text id={'tooltipContent'} size="caption">
                      {content}
                    </Text>
                  </Box>
                ) : (
                  content
                )}
              </div>
            </div>
          </Animation>
        </Portal>
      )}
    </>
  );
};
